import React, { useEffect, useRef } from 'react';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import type { ChartRef } from '@/pages/dashboard/components/Chart/_config/interface';
import {
  normalizeTrendSeries,
  shouldRenderTrendChart,
  buildTrendTooltipFormatter,
  buildTrendChartConfig,
} from './chartUtils';
import type { ILiveMetricTrendChartProps } from './types';
import './index.scss';

export const LIVE_METRIC_TREND_CHART_HEIGHT = 98;

const LiveMetricTrendChart: React.FC<ILiveMetricTrendChartProps> = ({
  title,
  trendData,
  formatter,
  unit,
  height = LIVE_METRIC_TREND_CHART_HEIGHT,
  className = 'dbs-live-metric-trend-chart',
  tooltipClassName = 'dbs-live-metric-trend-chart-tooltip',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ChartRef>(null);

  const normalizedSeries = React.useMemo(
    () => normalizeTrendSeries(trendData?.series),
    [trendData?.series],
  );

  // Flex columns settle after first paint; resize keeps canvas within column width.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const resizeChart = (): void => {
      chartRef.current?.instance()?.resize({
        animation: { duration: 300 },
      });
    };

    const observer = new ResizeObserver(() => {
      resizeChart();
    });
    observer.observe(container);
    resizeChart();

    return () => observer.disconnect();
  }, [trendData]);

  if (!shouldRenderTrendChart(trendData)) {
    return null;
  }

  return (
    <div className={className} ref={containerRef}>
      <LineChart
        ref={chartRef}
        height={height}
        xAxisData={trendData?.xAxisData || []}
        series={normalizedSeries}
        isShowLegend={false}
        formatterFunc={buildTrendTooltipFormatter({ title, formatter, unit })}
        setOtherConfigFunc={buildTrendChartConfig({ formatter, unit, tooltipClassName })}
      />
    </div>
  );
};

export default LiveMetricTrendChart;
